/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-select components
import Select, { components as selectComponents } from "react-select";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

// Custom styles for SoftSelect
import styles from "components/SoftSelect/styles";

// const SoftSelect = forwardRef(({ 
//   size = "medium", 
//   error = false, 
//   success = false, 
//   menuPortalTarget = true, // ✅ NEW PROP ADDED
//   ...rest 
// }, ref) => {
//   const { light } = colors;

//   // ✅ ENHANCED STYLES WITH Z-INDEX FIX
//   const enhancedStyles = (size, error, success) => {
//     const baseStyles = styles(size, error, success);
//     return {
//       ...baseStyles,
//       menu: (provided) => ({
//         ...baseStyles.menu(provided),
//         zIndex: 9999,
//       }),
//       menuPortal: (provided) => ({
//         ...provided,
//         zIndex: 9999,
//       }),
//     };
//   };

//   return (
//     <Select
//       {...rest}
//       ref={ref}
//       styles={enhancedStyles(size, error, success)}
//       theme={(theme) => ({
//         ...theme,
//         colors: {
//           ...theme.colors,
//           primary25: light.main,
//           primary: light.main,
//         },
//       })}
//       menuPortalTarget={menuPortalTarget ? document.body : undefined} // ✅ PORTAL TO BODY
//       menuPosition="fixed" // ✅ FIXED POSITIONING
//     />
//   );
// });


import SoftBox from "components/SoftBox";

const ClearIndicator = (props) => {
  const clearAndClose = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Let react-select clear both its internal input state and the controlled value.
    // Calling onChange directly leaves the selected option rendered behind the control.
    props.clearValue();
    props.selectProps.closeMenuAfterClear();
    window.requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    });
  };

  return (
    <selectComponents.ClearIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: clearAndClose,
        onTouchEnd: clearAndClose,
      }}
    />
  );
};

ClearIndicator.propTypes = {
  clearValue: PropTypes.func.isRequired,
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.shape({
    closeMenuAfterClear: PropTypes.func.isRequired,
  }).isRequired,
};

const SoftSelect = forwardRef((
  {
    size = "medium",
    error = false,
    success = false,
    compact = false,
    compactMenu = false,
    menuPortalTarget = true,
    onMenuOpen,
    onMenuClose,
    sx = {},
    ...rest
  },
  ref
) => {
  const { light } = colors;
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const handleMenuOpen = () => {
    setMenuIsOpen(true);
    if (onMenuOpen) onMenuOpen();
  };

  const handleMenuClose = () => {
    setMenuIsOpen(false);
    if (onMenuClose) onMenuClose();
  };

  const enhancedStyles = (size, error, success) => {
    const baseStyles = styles(size, error, success, compact, compactMenu);
    return {
      ...baseStyles,
      control: (provided, state) => ({
        ...baseStyles.control(provided, state),
        width: "100%", // ✅ full width inside container
      }),
      menu: (provided) => ({
        ...baseStyles.menu(provided),
        zIndex: 9999,
        minWidth: "100%", // ✅ match control width
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    };
  };

  return (
    <SoftBox sx={{ width: "100%", ...sx }}>
      <Select
        {...rest}
        ref={ref}
        components={{ ClearIndicator, ...(rest.components || {}) }}
        closeMenuAfterClear={() => setMenuIsOpen(false)}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        styles={enhancedStyles(size, error, success)}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: light.main,
            primary: light.main,
          },
        })}
        menuPortalTarget={menuPortalTarget ? document.body : undefined}
        menuPosition="fixed"
      />
    </SoftBox>
  );
});


// Typechecking props for the SoftSelect
SoftSelect.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  error: PropTypes.bool,
  compact: PropTypes.bool,
  compactMenu: PropTypes.bool,
  sx: PropTypes.object,
  success: PropTypes.bool,
  menuPortalTarget: PropTypes.bool, // ✅ NEW PROP TYPE
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
};

export default SoftSelect;
